import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { DEFAULT_BRANCH_ID } from "../../../constant";
import CommonSelect from "../../../core/common/commonSelect";
import Table from "../../../core/common/dataTable/index";
import MultiSelectSection, { Option } from "../../../core/common/multiSelect";
import {
  activeList,
  CLASSES_SORT_OPTIONS,
  registrationTypeOptions,
} from "../../../core/common/selectoption/selectoption";
import TooltipOption from "../../../core/common/tooltipOption";
import { selectSystemsForCurrentBranch } from "../../../core/data/redux/slice/systemsSlice";
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice";
import { campusBrandName, campusBranchLabel } from "../../../utils/campusLogos";
import { selectCurrentSession } from "../../../core/data/redux/slice/sessionsSlice";
import {
  Class,
  createClass,
  getAllClasses,
  getAllSections,
  createSection,
  deleteSection,
  PaginationData,
  Sections,
  updateClass,
} from "../../../service/classes";
import { all_routes } from "../../router/all_routes";
import DeleteClassModal from "./deleteModal";
import RoomService, { Room, AssignedRoom } from "../../../service/class-room";

// Fixed per-board Stage options (mirrors RMS exactly) — not admin-configurable,
// only which one applies to a given class is a choice.
const STAGE_OPTIONS_BY_SYSTEM_CODE: Record<string, string[]> = {
  MATRIC: ["Pre-primary", "Primary", "Lower Secondary", "Secondary"],
  OLEVEL: ["Stage 1", "Stage 2", "Stage 3", "Stage 4", "Stage 5"],
  AKU: ["Secondary"],
  COLLEGE: ["Higher Secondary"],
};

const Classes = () => {
  const systems = useSelector(selectSystemsForCurrentBranch);
  const currentBranch = useSelector(selectCurrentBranch);
  const currentSession = useSelector(selectCurrentSession);
  const componentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [allClass, setAllClass] = useState<Class[]>([]);
  const [filterClass, setFilterClass] = useState<Class[]>([]);
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);
  const [pagination, setPagination] = useState({ limit: 10, page: 1 });
  const [paginationData, setPaginationData] = useState<PaginationData>();
  const [sortBy, setSortBy] = useState(CLASSES_SORT_OPTIONS[0]);
  const [searchText, setSearchText] = useState("");
  const [selectedSections, setSelectedSections] = useState<Option[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sectionOptions, setSectionOptions] = useState<Option[]>([]);
  const [creatingSection, setCreatingSection] = useState(false);

  // Type-to-create a section from the Section dropdown. The backend creates it
  // for ALL campuses for the current session, then we add it to the options and
  // return it so the caller can select it.
  const handleCreateSection = async (name: string): Promise<Option | null> => {
    const trimmed = name.trim();
    if (!trimmed) return null;
    if (!currentSession?._id) {
      toast.error("Select an academic year first");
      return null;
    }
    setCreatingSection(true);
    try {
      const created = await createSection({ name: trimmed, session: currentSession._id });
      const opt: Option = { label: created.name, value: created._id };
      setSectionOptions((prev) => (prev.some((o) => o.value === opt.value) ? prev : [...prev, opt]));
      toast.success(`Section "${trimmed}" added for all campuses`);
      return opt;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to add section");
      return null;
    } finally {
      setCreatingSection(false);
    }
  };

  // Delete a section from the dropdown. Blocked by the backend if students are
  // enrolled in it (returns a clear message).
  const handleDeleteSection = async (opt: Option) => {
    if (!window.confirm(`Delete section "${opt.label}"?`)) return;
    try {
      await deleteSection(opt.value);
      setSectionOptions((prev) => prev.filter((o) => o.value !== opt.value));
      setSelectedSections((prev) => prev.filter((o) => o.value !== opt.value));
      toast.success(`Section "${opt.label}" deleted`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to delete section");
    }
  };
  const [allRooms, setAllRooms] = useState<Room[]>([]);
  const [assignedRooms, setAssignedRooms] = useState<AssignedRoom[]>([]);
  const [editingRoom, setEditingRoom] = useState<Option | null>(null);
  const [editingRoomAssignmentId, setEditingRoomAssignmentId] = useState<string | null>(null);
  const [sectionRoomAssignments, setSectionRoomAssignments] = useState<
    { sectionId: string; room: Option | null }[]
  >([]);
  const [editingSectionRoomAssignments, setEditingSectionRoomAssignments] = useState<
    { sectionId: string; room: Option | null; assignmentId?: string }[]
  >([]);

  const [newClassData, setNewClassData] = useState({
    name: "",
    capacity: "",
    registrationType: "interview+test",
    stage: "",
    branchId: DEFAULT_BRANCH_ID,
    status: "active",
    system: "",
    order: 0,
  });
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [filters, setFilters] = useState<{
    class?: Option;
    sections?: Option;
    status?: Option;
    system?: Option;
  } | null>(null);

  const [appliedFilters, setAppliedFilters] = useState<{
    class?: string;
    sections?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    system?: string;
  } | null>({ system: systems[0]?._id });

  // "Class"-style naming (Matric/AKU/College) vs "Grade"-style (Cambridge O-Level).
  // Falls back to true (Class-style) if the system's style hasn't loaded yet.
  const isClassStyleSystem =
    systems.find((s) => s._id === appliedFilters?.system)?.style !== "cambridge";
  const currentSystemCode = systems.find((s) => s._id === appliedFilters?.system)?.code || "";
  const stageOptions = STAGE_OPTIONS_BY_SYSTEM_CODE[currentSystemCode] || [];

  const handlePrint = useReactToPrint({ contentRef: componentRef });

  const handleApplyClick = () => {
    if (dropdownMenuRef.current) {
      dropdownMenuRef.current.classList.remove("show");
    }
    setAppliedFilters({
      class: filters?.class?.value as string,
      sections: filters?.sections?.value as string,
      status: filters?.status?.value as string,
      system: appliedFilters?.system,
    });
  };

  const fetchAssignedRoomsForClass = async (classId: string) => {
    try {
      const response = await RoomService.getAssignedRooms({
        class: classId,
      });
      return response.assignedRooms || [];
    } catch (error) {
      return [];
    }
  };

  const fetchClasses = useCallback(async () => {
    setLoading(true);
    try {
      const [classesResponse, assignedData, availableRooms] = await Promise.all([
        getAllClasses({
          ...pagination,
          ...appliedFilters,
          ...sortBy.value,
          searchText,
          session: currentSession?._id,
        }),
        RoomService.getAssignedRooms(),
        RoomService.fetchAvailableRooms(),
      ]);

      const assignedRoomsList = assignedData.assignedRooms || [];


      const normalizedAssignedRooms = assignedRoomsList.map((ar: any) => {
        if (!ar.section) {
          return {
            ...ar,
            assignmentId: ar.assignmentId || ar._id,
            room: null,
            class: typeof ar.class === 'object' ? ar.class?._id : ar.class
          };
        }

        if (typeof ar.room === 'string' && ar.name) {
          return {
            ...ar,
            assignmentId: ar.assignmentId || ar._id,
            room: {
              _id: ar.room,
              name: ar.name,
              capacity: ar.capacity || 0,
              floor: ar.floor || '',
              type: ar.type || 'classroom',
            },
            class: typeof ar.class === 'object' ? ar.class?._id : ar.class
          };
        }

        if (ar.room && typeof ar.room === 'object' && ar.room._id) {
          return {
            ...ar,
            assignmentId: ar.assignmentId || ar._id,
            room: ar.room,
            class: typeof ar.class === 'object' ? ar.class?._id : ar.class
          };
        }

        return {
          ...ar,
          assignmentId: ar.assignmentId || ar._id,
          room: null,
          class: typeof ar.class === 'object' ? ar.class?._id : ar.class
        };
      });

      setAssignedRooms(normalizedAssignedRooms);
      setAllRooms(availableRooms);

      const classesWithRoom = classesResponse.classes.map((cls) => {
        const sectionsWithRoom = cls.sections.map((sec) => {
          const assigned = normalizedAssignedRooms.find(
            (ar) =>
              ar.section &&
              ar.section._id === sec._id &&
              ar.isActive &&
              ar.class === cls._id
          );

          return {
            ...sec,
            room: assigned?.room || null,
            assignedId: assigned?.assignedId || null,
            assignmentId: assigned?.assignmentId || null,
          };
        });
        return { ...cls, sections: sectionsWithRoom };
      });

      setAllClass(classesWithRoom);
      setPaginationData(classesResponse.pagination);

    } catch (error) {
      console.error("Error fetching classes:", error);
      setAllClass([]);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [pagination, appliedFilters, sortBy, searchText, currentSession?._id]);

  useEffect(() => {
    if (!currentSession?._id || !currentBranch?._id) return;
    const fetchInitialData = async () => {
      setInitialLoading(true);
      try {
        const [availableRooms, sectionsData] = await Promise.all([
          RoomService.fetchAvailableRooms(),
          // Scoped to this campus: sections are shared across every class on
          // one campus, but a class on Ancholi must never offer Naseerabad's
          // "A"/"B"/"C" as pickable options — they look identical in the list
          // and picking the wrong one attaches someone else's section here.
          getAllSections({ session: currentSession._id, branch: currentBranch._id, limit: 200 }),
        ]);

        setAllRooms(availableRooms);

        const formattedOptions = sectionsData.sections.map((section: any) => ({
          value: section._id,
          label: section.name,
        }));
        setSectionOptions(formattedOptions);
      } catch (err) {
        console.error("Error fetching initial data:", err);
      }
    };

    fetchInitialData();
  }, [currentSession?._id, currentBranch?._id]);

  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);
  useEffect(() => {
    fetchClasses();
  }, [
    pagination.page,
    pagination.limit,
    sortBy,
    searchText,
    appliedFilters?.system,
    appliedFilters?.class,
    appliedFilters?.sections,
    appliedFilters?.status
  ]);

  const handleInputChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | { target: { name: string; value: string } }
  ) => {
    const { name, value } = e.target;
    setNewClassData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const isRoomAlreadyAssigned = (roomId: string) => {
    return assignedRooms.some(ar =>
      ar.isActive &&
      ar.room &&
      typeof ar.room === 'object' &&
      ar.room._id === roomId
    );
  };

  const getAvailableRoomsForSection = (currentSectionId: string) => {
    const unassignedRooms = allRooms.filter((room) => !isRoomAlreadyAssigned(room._id));

    return unassignedRooms.filter((room) => {
      const isAssignedToOtherSectionInForm = sectionRoomAssignments.some(
        (assignment) =>
          assignment.sectionId !== currentSectionId &&
          assignment.room?.value === room._id
      );
      return !isAssignedToOtherSectionInForm;
    }).map((r) => ({ label: r.name, value: r._id }));
  };

  const getAvailableRoomsForSectionEdit = (currentSectionId: string) => {
    if (!editingClass) return [];

    try {
      const currentAssignment = assignedRooms.find(
        ar =>
          ar.section &&
          ar.section._id === currentSectionId &&
          ar.isActive &&
          ar.class === editingClass._id
      );
      const currentRoomId = currentAssignment?.room && typeof currentAssignment.room === 'object'
        ? (currentAssignment.room as Room)._id
        : null;
      const otherAssignedRoomIds = assignedRooms
        .filter(ar =>
          ar.isActive &&
          ar.section &&
          ar.section._id !== currentSectionId &&
          ar.room &&
          typeof ar.room === 'object' &&
          (ar.room as Room)._id &&
          ar.class !== editingClass._id
        )
        .map(ar => (ar.room as Room)._id);

      const formAssignedRoomIds = editingSectionRoomAssignments
        .filter(assignment =>
          assignment.sectionId !== currentSectionId &&
          assignment.room &&
          editingClass.sections.some(s => s._id === assignment.sectionId)
        )
        .map(assignment => assignment.room!.value);

      const availableRooms = allRooms.filter((room) => {
        if (currentRoomId === room._id) return true;
        if (otherAssignedRoomIds.includes(room._id)) return false;
        if (formAssignedRoomIds.includes(room._id)) return false;
        return true;
      });

      return availableRooms.map((r) => ({
        label: r.name,
        value: r._id
      }));
    } catch (error) {
      console.error("Error in getAvailableRoomsForSectionEdit:", error);
      return [];
    }
  };

  const handleCreateClass = async (e: React.FormEvent) => {
    e.preventDefault();

    if (loading) return;

    const selectedRooms = sectionRoomAssignments
      .map((assignment) => assignment.room?.value)
      .filter(Boolean) as string[];

    for (const roomId of selectedRooms) {
      if (isRoomAlreadyAssigned(roomId)) {
        const roomName = allRooms.find(r => r._id === roomId)?.name || 'Unknown Room';
        toast.error(`Room "${roomName}" is already assigned to another section!`);
        return;
      }
    }

    const uniqueRoomIds = Array.from(new Set(selectedRooms));
    if (selectedRooms.length !== uniqueRoomIds.length) {
      toast.error("Same room cannot be assigned to different sections");
      return;
    }

    setLoading(true);
    try {
      const createdClass = await createClass({
        ...newClassData,
        capacity: parseInt(newClassData.capacity),
        status: newClassData.status,
        registrationType: newClassData.registrationType,
        sections: selectedSections.map((item) => item.value),
        system: appliedFilters?.system,
      });

      if (selectedRooms.length > 0) {
        try {
          const roomAssignmentPromises = sectionRoomAssignments.map((assignment) => {
            if (assignment.room) {
              return RoomService.assignRoom(createdClass._id, assignment.sectionId, assignment.room.value);
            }
            return Promise.resolve();
          });

          await Promise.all(roomAssignmentPromises);

          const updatedAvailableRooms = await RoomService.fetchAvailableRooms();
          setAllRooms(updatedAvailableRooms);
          const el = document.getElementById("modal_confirm");
          if (el) {
            el.setAttribute("data-bs-dismiss", "modal");
            el.click();
            el.removeAttribute("data-bs-dismiss");
          }
          toast.success("Class created successfully!");
        } catch (roomError: any) {
          if (roomError.response?.data?.message?.includes("already assigned") ||
            roomError.response?.data?.message?.toLowerCase().includes("room")) {
            toast.error(`Class created but room assignment failed: ${roomError.response.data.message}`);
          } else {
            toast.error("Class created but room assignment failed");
          }
        }
      }

      await fetchClasses();


      resetClassForm();
    } catch (error: any) {
      if (error.response?.data?.message?.includes("already exist")) {
        toast.error("Class already exist with this class name and section");
      } else if (error.response?.data?.message?.includes("already assigned") ||
        error.response?.data?.message?.toLowerCase().includes("room")) {
        toast.error("Error creating class");
      } else {
        toast.error("Error creating class");
        console.error("Error creating class:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const resetClassForm = () => {
    setNewClassData({
      name: "",
      capacity: "",
      registrationType: "interview+test",
      stage: "",
      branchId: DEFAULT_BRANCH_ID,
      status: "active",
      system: "",
      order: 0,
    });
    setSelectedSections([]);
    setSectionRoomAssignments([]);
  };

  const handleUpdateClass = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingClass) return;

    const selectedRooms = editingSectionRoomAssignments
      .map((assignment) => assignment.room?.value)
      .filter(Boolean) as string[];

    const currentSectionIds = editingSectionRoomAssignments.map(a => a.sectionId);

    const otherAssignedRooms = assignedRooms
      .filter(ar =>
        ar.isActive &&
        ar.section &&
        !currentSectionIds.includes(ar.section._id) &&
        ar.room &&
        typeof ar.room === 'object' &&
        (ar.room as Room)._id
      )
      .map(ar => (ar.room as Room)._id);

    for (const roomId of selectedRooms) {
      if (otherAssignedRooms.includes(roomId)) {
        const roomName = allRooms.find(r => r._id === roomId)?.name || 'Unknown Room';
        toast.error(`Room "${roomName}" is already assigned to another section!`);
        return;
      }
    }

    const uniqueRoomIds = Array.from(new Set(selectedRooms));
    if (selectedRooms.length !== uniqueRoomIds.length) {
      toast.error("Same room cannot be assigned to different sections");
      return;
    }

    setLoading(true);
    try {
      await updateClass(editingClass._id, {
        ...editingClass,
        capacity: parseInt(editingClass.capacity.toString()),
        sections: editingClass.sections.map((item) => item._id),
        session: currentSession?._id,
      });

      const roomUpdatePromises = editingSectionRoomAssignments.map((assignment) => {
        const { sectionId, room, assignmentId } = assignment;

        if (room) {
          if (assignmentId) {
            return RoomService.updateRoomAssignment(assignmentId, room.value);
          } else {
            return RoomService.assignRoom(editingClass._id, sectionId, room.value);
          }
        } else {
          if (assignmentId) {
            return RoomService.deleteRoomAssignment(assignmentId);
          }
          return Promise.resolve(null);
        }
      });

      await Promise.all(roomUpdatePromises);
      const el = document.getElementById("edit_class");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
      toast.success("Class updated successfully!");

      await fetchClasses();



      setEditingClass(null);
      setEditingSectionRoomAssignments([]);

    } catch (error: any) {

      if (error.response?.data?.message?.includes("already assigned") ||
        error.response?.data?.message?.toLowerCase().includes("room")) {
        toast.error(error.response.data.message || "Room assignment failed!");
      } else if (error.response?.data?.message?.includes("not found")) {
        toast.error("Room assignment not found. Please try again.");
      } else if (error.response?.status === 400) {
        toast.error("Bad request. Please check your data.");
      } else if (error.response?.status === 404) {
        toast.error("Resource not found.");
      } else if (error.response?.status === 500) {
        toast.error("Server error. Please try again later.");
      } else {
        toast.error("Error updating class");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = async (classItem: Class) => {
    setEditingClass(classItem);

    try {
      const classAssignedRooms = await fetchAssignedRoomsForClass(classItem._id);

      const initialAssignments = classItem.sections.map((section) => {
        const assignedRoom = classAssignedRooms.find(
          (ar) =>
            ar.section &&
            ar.section._id === section._id &&
            ar.isActive &&
            (typeof ar.class === 'object' ? ar.class._id === classItem._id : ar.class === classItem._id)
        );

        let roomOption: Option | null = null;
        if (assignedRoom) {
          let roomId = '';
          let roomName = '';

          if (typeof assignedRoom.room === 'string') {
            roomId = assignedRoom.room;
            const roomObj = allRooms.find(r => r._id === assignedRoom.room);
            if (roomObj) {
              roomName = roomObj.name;
            } else {

              roomName = assignedRoom.name || 'Room';
            }
          }

          else if (assignedRoom.room && typeof assignedRoom.room === 'object' && (assignedRoom.room as Room)._id) {
            const room = assignedRoom.room as Room;
            roomId = room._id;
            roomName = room.name || assignedRoom.name || 'Room';
          }

          else if (assignedRoom.name) {
            roomId = typeof assignedRoom.room === 'string' ? assignedRoom.room : '';
            roomName = assignedRoom.name;
          }

          if (roomId && roomName) {
            roomOption = {
              label: roomName,
              value: roomId
            };
          }
        }

        return {
          sectionId: section._id,
          room: roomOption,
          assignmentId: assignedRoom?.assignmentId || undefined
        };
      });

      setEditingSectionRoomAssignments(initialAssignments);

      const updatedAssignedRooms = await RoomService.getAssignedRooms();

      const normalizedUpdatedRooms = updatedAssignedRooms.assignedRooms?.map((ar: any) => {
        if (typeof ar.room === 'string' && ar.name) {
          return {
            ...ar,
            assignmentId: ar.assignmentId || ar._id,
            room: {
              _id: ar.room,
              name: ar.name,
              capacity: ar.capacity || 0,
              floor: ar.floor || '',
              type: ar.type || 'classroom',
            },
            class: typeof ar.class === 'object' ? ar.class._id : ar.class
          };
        }
        return ar;
      }) || [];

      setAssignedRooms(normalizedUpdatedRooms);

    } catch (error) {
      const initialAssignments = classItem.sections.map((section) => {
        const assignedRoom = assignedRooms.find(
          (ar) =>
            ar.section &&
            ar.section._id === section._id &&
            ar.isActive &&
            ar.class === classItem._id
        );

        let roomOption: Option | null = null;
        if (assignedRoom?.room && typeof assignedRoom.room === 'object' && (assignedRoom.room as Room)._id) {
          const room = assignedRoom.room as Room;
          roomOption = {
            label: room.name || 'Room',
            value: room._id
          };
        }

        return {
          sectionId: section._id,
          room: roomOption,
          assignmentId: assignedRoom?.assignmentId || undefined
        };
      });

      setEditingSectionRoomAssignments(initialAssignments);
    }

    setEditingRoom(null);
    setEditingRoomAssignmentId(null);
  };

  const handleChange = (page: number, pageSize: number) => {
    setPagination({ limit: pageSize, page: page });
  };

  const handleEditInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setEditingClass((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSortSelect = (item: {
    label: string;
    value: {
      sortBy: string;
      sortOrder: string;
    };
  }) => {
    setSortBy(item);
  };

  const handleResetFilters = () => {
    setFilters({
      class: {
        label: "Select",
        value: "",
      },
      sections: {
        label: "Select",
        value: "",
      },
      status: {
        label: "Select",
        value: "",
      },
    });
    setAppliedFilters({ system: appliedFilters?.system });
  };

  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      allClass.map((item) => ({
        ID: item._id,
        Class: item.name,
        Section: item.sections.map(s => s.name).join(', '),
        Room: item.sections.map(s => {
          const assigned = assignedRooms.find(ar =>
            ar.section && ar.section._id === s._id && ar.isActive
          );
          if (assigned?.room && typeof assigned.room === 'object') {
            return (assigned.room as Room).name;
          }
          return 'Not Assigned';
        }).join(', '),
        "No of Student": item.capacity,
        Status: item.status,
        Subject: item.subjects?.join(', ') || '',
      }))
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Classes");
    XLSX.writeFile(wb, "classes.xlsx");
  };
const availableRooms = async () => {
  try {
    const availableRooms = await RoomService.fetchAvailableRooms();
    setAllRooms(availableRooms);
  } catch (error) {
    console.error("Error refreshing available rooms:", error);
  }
};

  const route = all_routes;
  const columns = [
    {
      title:
        isClassStyleSystem ? "Class" : "Grade",
      dataIndex: "name",
      sorter: (a: Class, b: Class) => a.name.length - b.name.length,
    },
    {
      title: "Sections",
      dataIndex: "sections",
      sorter: (a: Class, b: Class) => a.sections.length - b.sections.length,
      render: (sections: Sections) => (
        <div style={{ display: "flex", gap: "5px" }}>
          {sections?.map((item) => (
            <span
              key={item._id}
              className="badge badge-soft-success d-inline-flex align-items-center"
              style={{ display: "flex", gap: 5 }}
            >
              {item.name}
            </span>
          ))}
        </div>
      ),
    },
    {
      title: "No of Student (Allowed)",
      dataIndex: "capacity",
      sorter: (a: Class, b: Class) => a.capacity - b.capacity,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <>
          {text === "active" ? (
            <span className="badge badge-soft-success d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              {text}
            </span>
          ) : (
            <span className="badge badge-soft-danger d-inline-flex align-items-center">
              <i className="ti ti-circle-filled fs-5 me-1"></i>
              {text}
            </span>
          )}
        </>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text: string, record: Class) => (
        <>
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#edit_class"
                    onClick={() => handleEditClick(record)}
                  >
                    <i className="ti ti-edit-circle me-2" />
                    Edit
                  </Link>
                </li>
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#delete-modal"
                    onClick={() => setSelectedId(record._id)}
                  >
                    <i className="ti ti-trash-x me-2" />
                    Delete
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </>
      ),
    },
  ];

  const isLoading = loading || initialLoading;

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {isClassStyleSystem
                  ? "Classes List"
                  : "Grades List"}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={route.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">
                      {" "}
                      {isClassStyleSystem
                        ? "Classes"
                        : "Grades"}{" "}
                    </Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isClassStyleSystem
                      ? "All Classes"
                      : "All Grades"}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <TooltipOption
                onRefresh={fetchClasses}
                onExportPdf={handlePrint}
                onExportExcel={handleExportExcel}
              />
              <div className="mb-2">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_class"
                >
                  <i className="ti ti-square-rounded-plus-filled me-2" />
                  {isClassStyleSystem
                    ? "Add Class"
                    : "Add Grade"}
                </Link>
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">
                {" "}
                {isClassStyleSystem
                  ? "Classes List"
                  : "Grades List"}
              </h4>
              <div className="d-flex align-items-center flex-wrap">
                <div className="mb-3 me-2">
                  <CommonSelect
                    className="select"
                    options={systems}
                    value={systems.find(
                      (item) => item._id === appliedFilters?.system
                    )}
                    getOptionValue={(item) => item._id}
                    getOptionLabel={(item) => item.name}
                    onChange={(systemItem) =>
                      setAppliedFilters((prev) => ({
                        ...prev,
                        system: systemItem?._id,
                      }))
                    }
                  />
                </div>
                <div className="dropdown mb-3 me-2">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                  >
                    <i className="ti ti-filter me-2" />
                    Filter
                  </Link>
                  <div
                    className="dropdown-menu drop-width"
                    ref={dropdownMenuRef}
                  >
                    <form>
                      <div className="d-flex align-items-center border-bottom p-3">
                        <h4>Filter</h4>
                      </div>
                      <div className="p-3 border-bottom pb-0">
                        <div className="row">
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Class</label>
                              {isLoading ? (
                                <div> Loading classes... </div>
                              ) : (
                                <CommonSelect
                                  className="select"
                                  options={filterClass.map((cls) => ({
                                    label: cls.name,
                                    value: cls._id,
                                  }))}
                                  value={filters?.class}
                                  onChange={(classItem) =>
                                    setFilters((prev) => ({
                                      ...prev,
                                      class: classItem as Option,
                                    }))
                                  }
                                />
                              )}
                            </div>
                          </div>
                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Section</label>
                              <CommonSelect
                                className="select"
                                options={sectionOptions}
                                value={filters?.sections}
                                onChange={(sectionItem) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    sections: sectionItem as Option,
                                  }))
                                }
                              />
                            </div>
                          </div>

                          <div className="col-md-12">
                            <div className="mb-3">
                              <label className="form-label">Status</label>
                              <CommonSelect
                                className="select"
                                options={activeList}
                                value={filters?.status}
                                onChange={(statusItem) =>
                                  setFilters((prev) => ({
                                    ...prev,
                                    status: statusItem as Option,
                                  }))
                                }
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="p-3 d-flex align-items-center justify-content-end">
                        <Link
                          to="#"
                          className="btn btn-light me-3"
                          onClick={handleResetFilters}
                        >
                          Reset
                        </Link>
                        <Link
                          to="#"
                          className="btn btn-primary"
                          onClick={handleApplyClick}
                        >
                          Apply
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="dropdown mb-3">
                  <Link
                    to="#"
                    className="btn btn-outline-light bg-white dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-sort-ascending-2 me-2" />
                    Sort by {sortBy.label}
                  </Link>
                  <ul className="dropdown-menu p-3">
                    {CLASSES_SORT_OPTIONS.map((item, index) => (
                      <li key={index}>
                        <div
                          className={`dropdown-item rounded-1 ${item.label === sortBy.label ? "active" : ""
                            }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleSortSelect(item)}
                        >
                          {item.label}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div
              className="card-body p-0 py-3 printable-content"
              ref={componentRef}
            >
              <div className="print-only-header">
                <div className="school-brand">
                  <img src={currentBranch?.blackLogoUrl || "/assets/img/icons/logo.png"} alt={campusBrandName(currentBranch)} />
                  <strong>{campusBrandName(currentBranch)}{campusBranchLabel(currentBranch) ? ` - ${campusBranchLabel(currentBranch)}` : ""}</strong>
                </div>
                <div className="report-title">Classes</div>
                <div className="print-date">{new Date().toLocaleDateString("en-GB")}</div>
              </div>
              <Table
                columns={columns}
                dataSource={allClass}
                pagination={{ ...paginationData, onChange: handleChange }}
                loading={isLoading}
                setSearchText={setSearchText}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="add_class">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                {isClassStyleSystem
                  ? "Add Class"
                  : "Add Grade"}
              </h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetClassForm}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleCreateClass}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        {" "}
                        {isClassStyleSystem
                          ? "Class Name"
                          : "Grade Name"}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={newClassData.name}
                        onChange={handleInputChange}
                        required
                        placeholder={
                          isClassStyleSystem
                            ? "e.g Class 1"
                            : "e.g Grade 1"
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Section</label>
                      <MultiSelectSection
                        options={sectionOptions}
                        value={selectedSections}
                        isLoading={creatingSection}
                        onCreateOption={async (name) => {
                          const opt = await handleCreateSection(name);
                          if (opt) setSelectedSections((prev) => [...prev, opt]);
                        }}
                        onDeleteOption={handleDeleteSection}
                        onChange={(selected) => {
                          setSelectedSections(selected);
                          const updatedAssignments = selected.map((section) => {
                            const existingAssignment = sectionRoomAssignments.find(
                              (assignment) => assignment.sectionId === section.value
                            );
                            return existingAssignment || { sectionId: section.value, room: null };
                          });
                          const filteredAssignments = updatedAssignments.filter((assignment) =>
                            selected.some((section) => section.value === assignment.sectionId)
                          );

                          setSectionRoomAssignments(filteredAssignments);
                        }}
                      />
                    </div>
                    {selectedSections.length > 0 && (
                      <div className="mb-3">
                        <label className="form-label">Assign Rooms</label>
                        <div className="room-assignment-container">
                          {sectionRoomAssignments.map((assignment, index) => {
                            const section = selectedSections.find(
                              (s) => s.value === assignment.sectionId
                            );
                            if (!section) return null;

                            return (
                              <div key={assignment.sectionId} className="mb-3">
                                <label className="form-label mb-1">
                                  Room for Section: <span className="text-primary">{section.label}</span>
                                </label>
                                <CommonSelect
                                  options={getAvailableRoomsForSection(assignment.sectionId)}
                                  value={assignment.room}
                                  onChange={(room) => {
                                    const updatedAssignments = [...sectionRoomAssignments];
                                    updatedAssignments[index] = { ...assignment, room };
                                    setSectionRoomAssignments(updatedAssignments);
                                  }}
                                  placeholder="Select Room (Optional)"
                                />
                                {getAvailableRoomsForSection(assignment.sectionId).length === 0 && (
                                  <small className="text-danger">
                                    No rooms available. All rooms are already assigned to other sections.
                                  </small>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">Number Of Student</label>
                      <input
                        type="number"
                        className="form-control"
                        name="capacity"
                        value={newClassData.capacity}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Order</label>
                      <input
                        type="number"
                        className="form-control"
                        name="order"
                        value={newClassData.order}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Stage</label>
                      <select
                        className="form-select"
                        name="stage"
                        value={newClassData.stage}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">Select Stage</option>
                        {stageOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Registration Type</label>
                      <select
                        className="form-select"
                        name="registrationType"
                        value={newClassData.registrationType}
                        onChange={handleInputChange}
                        required
                      >
                        {registrationTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="status-title">
                        <h5>Status</h5>
                        <p>Change the Status by toggle</p>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="switch-sm"
                          checked={newClassData.status === "active"}
                          onChange={(e) => {
                            setNewClassData((prev) => ({
                              ...prev,
                              status: e.target.checked
                                ? "active"
                                : "inactive",
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={resetClassForm}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" id="modal_confirm">
                  {isClassStyleSystem
                    ? "Add Class"
                    : "Add Grade"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="modal fade" id="edit_class">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                {" "}
                {isClassStyleSystem
                  ? "Edit Class"
                  : "Edit Grade"}
              </h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={() => {
                  setEditingClass(null);
                  setEditingRoom(null);
                  setEditingRoomAssignmentId(null);
                  setEditingSectionRoomAssignments([]);
                }}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleUpdateClass}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">
                        {" "}
                        {isClassStyleSystem
                          ? "Class Name"
                          : "Grade Name"}
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="name"
                        value={editingClass?.name}
                        onChange={handleEditInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Section</label>
                      <MultiSelectSection
                        options={sectionOptions}
                        value={(editingClass?.sections || []).map((item) => ({
                          label: item.name,
                          value: item._id,
                        }))}
                        isLoading={creatingSection}
                        onCreateOption={async (name) => {
                          const opt = await handleCreateSection(name);
                          if (opt)
                            setEditingClass((prev: any) => ({
                              ...prev,
                              sections: [...(prev?.sections || []), { _id: opt.value, name: opt.label }],
                            }));
                        }}
                        onDeleteOption={handleDeleteSection}
                        onChange={(selected) => {
                          setEditingClass((prev: any) => ({
                            ...prev,
                            sections: selected.map((item) => ({
                              name: item.label,
                              _id: item.value,
                            })),
                          }));
                          const updatedAssignments = selected.map((section) => {
                            const existingAssignment = editingSectionRoomAssignments.find(
                              (assignment) => assignment.sectionId === section.value
                            );
                            return existingAssignment || { sectionId: section.value, room: null };
                          });

                          setEditingSectionRoomAssignments(updatedAssignments);
                        }}
                      />
                    </div>

                    {editingClass?.sections && editingClass.sections.length > 0 && (
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label mb-0">Assign Rooms</label>
                        </div>
                        <div className="room-assignment-container border rounded p-3">
                          {editingSectionRoomAssignments.map((assignment, index) => {
                            const section = editingClass.sections.find(
                              (s) => s._id === assignment.sectionId
                            );
                            if (!section) return null;

                            const availableRooms = getAvailableRoomsForSectionEdit(assignment.sectionId);

                            const currentAssignedRoom = assignedRooms.find(
                              ar =>
                                ar.section &&
                                ar.section._id === section._id &&
                                ar.isActive &&
                                ar.class === editingClass._id
                            );

                            return (
                              <div key={assignment.sectionId} className="mb-3 pb-3 border-bottom">
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <label className="form-label mb-0">
                                    <strong>Section:</strong> {section.name}
                                  </label>

                                </div>

                                <CommonSelect
                                  options={availableRooms}
                                  value={assignment.room}
                                  onChange={(room) => {
                                    const updatedAssignments = [...editingSectionRoomAssignments];
                                    updatedAssignments[index] = { ...assignment, room };
                                    setEditingSectionRoomAssignments(updatedAssignments);
                                  }}
                                  placeholder="Select Room (Optional)"
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="form-label">No of Students</label>
                      <input
                        type="number"
                        className="form-control"
                        name="capacity"
                        value={editingClass?.capacity}
                        onChange={handleEditInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Order</label>
                      <input
                        type="number"
                        className="form-control"
                        name="order"
                        value={editingClass?.order}
                        onChange={handleEditInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Stage</label>
                      <select
                        className="form-select"
                        name="stage"
                        value={editingClass?.stage || ""}
                        onChange={handleEditInputChange}
                        required
                      >
                        <option value="">Select Stage</option>
                        {stageOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Registration Type</label>
                      <select
                        className="form-select"
                        name="registrationType"
                        value={editingClass?.registrationType}
                        onChange={handleEditInputChange}
                        required
                      >
                        {registrationTypeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="status-title">
                        <h5>Status</h5>
                        <p>Change the Status by toggle </p>
                      </div>
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id="switch-sm2"
                          checked={editingClass?.status === "active"}
                          onChange={(e) => {
                            setEditingClass((prev: any) => ({
                              ...prev,
                              status: e.target.checked
                                ? "active"
                                : "inactive",
                            }));
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  onClick={() => {
                    setEditingClass(null);
                    setEditingRoom(null);
                    setEditingRoomAssignmentId(null);
                    setEditingSectionRoomAssignments([]);
                  }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <DeleteClassModal
        selectedId={selectedId}
        setSelectedId={setSelectedId}
        fetchData={fetchClasses}
        appliedFilters={appliedFilters}
        isClassStyleSystem={isClassStyleSystem}
        allClasses={allClass}
        assignedRooms={assignedRooms}
        availableRooms={availableRooms}
      />
    </div>
  );
};

export default Classes;